import { LanguageAvailable } from '@shared/services/language/models';

/**
 * Extracts the primary language code from a given language string.
 *
 * The function processes the input language string by trimming whitespace, converting it to lowercase,
 * and extracting the initial language code segment (typically 2-3 alphabetic characters) before any
 * delimiter (e.g., a hyphen) or the end of the string. If the input string does not contain a valid
 * language code, an error is thrown.
 *
 * @param {LanguageAvailable} language - The input language string to process.
 * It should represent a language code in a format such as "en", "en-US", or "fr-ca".
 *
 * @returns {string} The primary language code extracted from the input, consisting of
 * 2-3 alphabetic characters (e.g., "en" or "fr").
 *
 * @throws {TypeError} Throws a `TypeError` if the input string does not match
 * the expected format for extracting a valid language code.
 *
 * @example
 * ```typescript
 * const code1 = extractPrimaryLanguageCode("en-US"); // Returns "en"
 * const code2 = extractPrimaryLanguageCode(" fr-ca"); // Returns "fr"
 * const code3 = extractPrimaryLanguageCode("de"); // Returns "de"
 *
 * // Throws TypeError
 * const invalidCode = extractPrimaryLanguageCode("123-xyz");
 * ```
 *
 * @remarks
 * This function assumes the input follows a language code convention as defined by
 * standards like BCP 47 or ISO 639. The function does not validate whether the extracted
 * language code is part of any specific standard; it purely extracts based on the format.
 */
export const extractPrimaryLanguageCode = (language: LanguageAvailable): string => {
  const cleaned: string = language.trim().toLowerCase();
  const match: RegExpMatchArray | null = RegExp(/^[a-z]{2,3}(?=-|$)/).exec(cleaned);
  if (!match) {
    throw new TypeError(`Invalid language code format: ${language}`);
  }
  return match[0];
};
